import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  ReactNode,
  useRef,
} from "react";
import KpiService from "../../../services/registry/kpi.ts";
import { KpiFeedbackStatistic } from "../../../Models/KpiFeedbackStatistic.ts";
import { ErrorResponse } from "../../../Models/Error.ts";
import { callErrorToast } from "../../../utilities/utilities.ts";
import { defaultFetchParam, FetchParam } from "../../../Models/FetchParam.ts";
import { KpiShipmentStatisticFilter } from "../../../Models/KpiShipmentStatistic.ts";

const kpiService = new KpiService();

interface FeedbacksStatisticsProviderProps {
  children: ReactNode;
  filters: KpiShipmentStatisticFilter;
}

interface FeedbacksStatisticsContextValue {
  data: KpiFeedbackStatistic | null;
  loader: boolean;
  filters: KpiShipmentStatisticFilter;
}

const FeedbacksStatisticsContext = createContext<
  FeedbacksStatisticsContextValue | undefined
>(undefined);

const FeedbacksStatisticsProvider: React.FC<
  FeedbacksStatisticsProviderProps
> = ({ children, filters }) => {
  const [data, setData] = useState<KpiFeedbackStatistic | null>(null);
  const [loader, setLoader] = useState<boolean>(false);
  const [error, setError] =
    useState<ErrorResponse<KpiFeedbackStatistic> | null>(null);

  const prevFilters = useRef<KpiShipmentStatisticFilter>(filters);

  const getStatistics = (): void => {
    setLoader(true);
    const params: FetchParam = {
      ...defaultFetchParam,
      search: [
        {
          selector: "date",
          value: [filters.start, filters.end],
        },
      ],
    };
    kpiService
      .getStatistic(params)
      .then((res) => {
        setData(res.data);
        setLoader(false);
      })
      .catch((err) => {
        callErrorToast(err);
        setLoader(false);
        setError(err);
      });

    return;
  };

  useEffect(() => {
    if (error) {
      callErrorToast(error);
    }
  }, [error]);

  useEffect(() => {
    if (
      filters &&
      JSON.stringify(prevFilters.current) !== JSON.stringify(filters)
    ) {
      getStatistics();
      prevFilters.current = filters;
    }
  }, [filters]);

  useEffect(() => {
    getStatistics();
  }, []);

  return (
    <FeedbacksStatisticsContext.Provider
      value={{
        data,
        loader,
        filters,
      }}
    >
      {children}
    </FeedbacksStatisticsContext.Provider>
  );
};

const useFeedbacksStatisticsContext = (): FeedbacksStatisticsContextValue => {
  const context = useContext(FeedbacksStatisticsContext);
  if (!context) {
    throw new Error(
      "useFeedbacksStatisticsContext must be used within a FeedbacksStatisticsProvider"
    );
  }
  return context;
};

// Export the provider and custom hook for use in other components
export { FeedbacksStatisticsProvider, useFeedbacksStatisticsContext };
